import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { fitNavItems } from '../lib/fitNavItems.ts';

type RoutePath = '/' | '/squad' | '/fixtures' | '/tracker' | '/compare' | '/leagues' | '/record';

interface NavLinkEntry {
  kind: 'link';
  to: RoutePath;
  label: string;
  exact: boolean;
}

interface NavMenuEntry {
  kind: 'menu';
  label: string;
  children: ReadonlyArray<{ to: RoutePath; label: string }>;
}

type NavEntry = NavLinkEntry | NavMenuEntry;

/** Four top-level entries (T-082), not seven — T-075 already turned `/` into
 * a desk that links onward to everything else, so a flat list of equal-weight
 * destinations no longer describes how the app is used. Record sits under
 * League deliberately: the advice record is scored gameweek by gameweek
 * against how the manager is actually doing, and T-058 already built it on
 * top of the mini-league work. */
const NAV_ITEMS: readonly NavEntry[] = [
  { kind: 'link', to: '/', label: 'This Week', exact: true },
  { kind: 'link', to: '/squad', label: 'My Squad', exact: false },
  {
    kind: 'menu',
    label: 'Research',
    children: [
      { to: '/fixtures', label: 'Fixtures' },
      { to: '/compare', label: 'Compare' },
    ],
  },
  {
    kind: 'menu',
    label: 'League',
    children: [
      { to: '/tracker', label: 'Tracker' },
      { to: '/leagues', label: 'Leagues' },
      { to: '/record', label: 'Record' },
    ],
  },
];

function isPathWithin(pathname: string, to: RoutePath, exact: boolean): boolean {
  if (exact) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

function isEntryActive(entry: NavEntry, pathname: string): boolean {
  if (entry.kind === 'link') return isPathWithin(pathname, entry.to, entry.exact);
  return entry.children.some((child) => isPathWithin(pathname, child.to, false));
}

interface MenuSection {
  heading?: string;
  links: ReadonlyArray<{ to: RoutePath; label: string }>;
}

/**
 * The masthead's nav (T-048, reworked by T-082). Grouped into four top-level
 * entries; whatever doesn't fit the container collapses into "More", in
 * order — the nav never scrolls and never clips.
 *
 * Widths are measured at runtime, not guessed from a breakpoint: a hidden
 * probe row (`.main-nav-measure`, `position: absolute` so it never affects
 * layout, wrapped in `.main-nav-measure-clip` so its real — unclipped —
 * content can never widen the document's own scroll extent) renders one
 * exact replica of every entry, caret included, and a `ResizeObserver` on
 * the real container re-measures on every resize — the fold, the unfold and
 * rotation all go through the same code path. `fitNavItems` is given the
 * container's flex `gap` alongside the widths, since a row of N entries
 * carries N-1 of them. Before the first measurement, and anywhere
 * `ResizeObserver` doesn't exist, `fitCount` stays `null` and every entry
 * renders — the failure mode is the old always-visible nav, not one with
 * items missing and no way to reach them.
 *
 * The very first measurement happens synchronously inside `useLayoutEffect`,
 * before the browser paints, so a narrow screen never flashes all four
 * entries before collapsing — there is no visible reflow on load.
 */
export function MainNav() {
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement>(null);
  const itemProbeRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const moreProbeRef = useRef<HTMLSpanElement>(null);
  const triggerRefs = useRef(new Map<string, HTMLButtonElement | null>());

  const [fitCount, setFitCount] = useState<number | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const moreProbe = moreProbeRef.current;
    if (!nav || !moreProbe) return;
    // No ResizeObserver means no way to react to a later resize, fold or
    // rotation — measuring once and going stale is worse than never
    // measuring at all, so this environment always gets every entry.
    if (typeof ResizeObserver === 'undefined') return;

    function remeasure() {
      if (!nav || !moreProbe) return;
      const containerWidth = nav.getBoundingClientRect().width;
      // A zero-width read means there's no real layout to act on yet (or
      // none at all, as in tests without a layout engine) — leave fitCount
      // as-is rather than collapsing everything on bogus data.
      if (containerWidth <= 0) return;
      const itemWidths = itemProbeRefs.current.map((el) => el?.getBoundingClientRect().width ?? 0);
      const moreWidth = moreProbe.getBoundingClientRect().width;
      // Read the real `.main-nav` gap rather than hardcoding it — it's the
      // same gap the probes render with, so this stays correct even if the
      // CSS value changes without this file being touched.
      const gap = Number.parseFloat(getComputedStyle(nav).columnGap) || 0;
      setFitCount(fitNavItems(containerWidth, itemWidths, moreWidth, gap));
    }

    remeasure();
    const observer = new ResizeObserver(remeasure);
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  // Closing on an outside click is one listener here, not one per menu —
  // only one menu is ever open at a time, so there's only ever one thing to
  // close. Skipped entirely while nothing's open.
  useEffect(() => {
    if (!openMenu) return;
    function onPointerDown(event: MouseEvent) {
      if (navRef.current?.contains(event.target as Node)) return;
      setOpenMenu(null);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [openMenu]);

  function onOpenChange(menu: string | null) {
    setOpenMenu(menu);
  }

  function onEscape(menu: string) {
    setOpenMenu(null);
    triggerRefs.current.get(menu)?.focus();
  }

  function registerTriggerRef(id: string, el: HTMLButtonElement | null) {
    triggerRefs.current.set(id, el);
  }

  const activeIndex = NAV_ITEMS.findIndex((item) => isEntryActive(item, pathname));
  const visibleCount = fitCount ?? NAV_ITEMS.length;
  const visibleItems = NAV_ITEMS.slice(0, visibleCount);
  const collapsedItems = NAV_ITEMS.slice(visibleCount);
  const activeIsCollapsed = activeIndex >= 0 && activeIndex >= visibleCount;

  return (
    <nav className="main-nav" aria-label="Main" ref={navRef}>
      {visibleItems.map((item) =>
        item.kind === 'link' ? (
          <Link
            key={item.to}
            to={item.to}
            className="main-nav-link"
            activeOptions={{ exact: item.exact }}
            activeProps={{ 'aria-current': 'page' }}
          >
            {item.label}
          </Link>
        ) : (
          <NavMenu
            key={item.label}
            id={item.label}
            label={item.label}
            isActive={isEntryActive(item, pathname)}
            sections={[{ links: item.children }]}
            open={openMenu === item.label}
            onOpenChange={onOpenChange}
            onEscape={onEscape}
            registerTriggerRef={registerTriggerRef}
          />
        ),
      )}

      {collapsedItems.length > 0 && (
        <NavMenu
          id="More"
          label="More"
          isActive={activeIsCollapsed}
          sections={collapsedItems.map((item) =>
            item.kind === 'link' ? { links: [{ to: item.to, label: item.label }] } : { heading: item.label, links: item.children },
          )}
          open={openMenu === 'More'}
          onOpenChange={onOpenChange}
          onEscape={onEscape}
          registerTriggerRef={registerTriggerRef}
        />
      )}

      {/* Off-flow (`position: absolute`, clipped to nothing by the wrapper
       * below — see styles.css) replica of every entry plus the More
       * trigger, always rendered — this is what actually gets measured. Real
       * elements come and go as `fitCount` changes, so they can't double as
       * their own measurement. Menu entries and More render the same caret
       * markup as the real trigger button (see `NavMenu` below): the caret
       * and the trigger's own internal gap both take up real width, and a
       * probe missing them under-measures every menu-shaped entry. */}
      <div className="main-nav-measure-clip">
        <div className="main-nav-measure" aria-hidden="true">
          {NAV_ITEMS.map((item, i) =>
            item.kind === 'menu' ? (
              <span
                key={item.label}
                ref={(el) => {
                  itemProbeRefs.current[i] = el;
                }}
                className="main-nav-trigger"
                data-probe-label={item.label}
              >
                {item.label}
                <span className="main-nav-caret">▾</span>
              </span>
            ) : (
              <span
                key={item.label}
                ref={(el) => {
                  itemProbeRefs.current[i] = el;
                }}
                className="main-nav-link"
                data-probe-label={item.label}
              >
                {item.label}
              </span>
            ),
          )}
          <span ref={moreProbeRef} className="main-nav-trigger" data-probe-label="More">
            More
            <span className="main-nav-caret">▾</span>
          </span>
        </div>
      </div>
    </nav>
  );
}

interface NavMenuProps {
  id: string;
  label: string;
  isActive: boolean;
  sections: ReadonlyArray<MenuSection>;
  open: boolean;
  onOpenChange: (menu: string | null) => void;
  onEscape: (menu: string) => void;
  registerTriggerRef: (id: string, el: HTMLButtonElement | null) => void;
}

/** One disclosure: a trigger button plus its panel. Used for "Research",
 * "League" and "More" alike — a real button with `aria-expanded`/
 * `aria-controls`, opened by click or Enter/Space (native button behaviour,
 * nothing extra needed), closed by Escape (with focus returned to the
 * trigger), by clicking outside (handled once, in `MainNav`, since only one
 * menu is ever open at a time) or by following a link inside. */
function NavMenu({ id, label, isActive, sections, open, onOpenChange, onEscape, registerTriggerRef }: NavMenuProps) {
  const panelId = `main-nav-panel-${id}`;

  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    onEscape(id);
  }

  return (
    <div className="main-nav-menu" onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={(el) => registerTriggerRef(id, el)}
        className="main-nav-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        aria-current={isActive ? 'page' : undefined}
        onClick={() => onOpenChange(open ? null : id)}
      >
        {label}
        <span aria-hidden="true" className="main-nav-caret">
          ▾
        </span>
      </button>

      {/* Plain links in a plain panel, not `role="menu"`/`menuitem` — that
       * ARIA pattern obliges arrow-key navigation between items, which this
       * disclosure doesn't implement, so claiming it would be worse than not
       * claiming it. A labelled, focusable link list needs nothing extra to
       * be usable by assistive tech. */}
      {open && (
        <div id={panelId} className="main-nav-panel">
          {sections.map((section, i) => (
            <div className="main-nav-panel-section" key={section.heading ?? i}>
              {section.heading && <span className="main-nav-panel-heading">{section.heading}</span>}
              {section.links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="main-nav-panel-link"
                  activeProps={{ 'aria-current': 'page' }}
                  onClick={() => onOpenChange(null)}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
